import { startTransition, useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  Books,
  Check,
  CheckCircle,
  CircleNotch,
  Cloud,
  Fire,
  GearSix,
  House,
  MapTrifold,
  Microphone,
  Sparkle,
  Stop,
  Timer,
  UploadSimple,
} from "@phosphor-icons/react";
import { loadBootstrap, replaceContentPack, saveProgress, saveSettings } from "./api";
import { transcribeAudio } from "./asr";
import type { ContentPack, Lesson, LessonStage } from "./content/defaultPack";
import type { BootstrapData, LessonProgress, UserSettings } from "./types";

type View = "today" | "practice" | "skills" | "library" | "settings";

function getInitialView(): View {
  const hash = window.location.hash.slice(1);
  return navigation.some((item) => item.id === hash) ? (hash as View) : "today";
}

const navigation: Array<{ id: View; label: string; icon: typeof House }> = [
  { id: "today", label: "Today", icon: House },
  { id: "practice", label: "Practice", icon: Microphone },
  { id: "skills", label: "Skill map", icon: MapTrifold },
  { id: "library", label: "Phrase vault", icon: Books },
  { id: "settings", label: "Settings", icon: GearSix },
];

type RabbitTone = "green" | "yellow" | "pink" | "purple";

const rabbitMarks: Record<RabbitTone, string> = {
  green: "/rabbit-mark.svg",
  yellow: "/rabbit-yellow-mark.svg",
  pink: "/rabbit-pink-mark.svg",
  purple: "/rabbit-purple-mark.svg",
};

function RabbitAssistant({ compact = false, tone = "green" }: { compact?: boolean; tone?: RabbitTone }) {
  if (compact) {
    return (
      <div className={`rabbit compact ${tone}`} aria-label={`${tone} rabbit lab assistant`}>
        <img src={rabbitMarks[tone]} alt="" />
      </div>
    );
  }

  return <img className="rabbit-hero" src="/rabbit-cheer.svg" alt="Cheering rabbit lab assistant" />;
}

const laneTones: RabbitTone[] = ["green", "yellow", "purple", "pink"];

const confettiPieces = Array.from({ length: 18 }, (_, index) => ({
  left: (index * 53 + 7) % 100,
  delay: (index % 6) * 0.32,
  color: ["#62bd52", "#f2c94c", "#ee91ad", "#8b70c9"][index % 4],
  round: index % 2 === 0,
}));

function scaledMinutes(minutes: number, dailyMinutes: number): number {
  return Math.max(1, Math.round((minutes * dailyMinutes) / 12));
}

function dateKey(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const rowLabels = ["日", "一", "二", "三", "四", "五", "六"];

function PracticeHeatmap({ activity }: { activity: Record<string, number> }) {
  const totalWeeks = 18;
  const todayKey = dateKey();
  const thisSunday = new Date();
  thisSunday.setDate(thisSunday.getDate() - thisSunday.getDay());

  const weeks: string[][] = [];
  for (let week = totalWeeks - 1; week >= 0; week -= 1) {
    const days: string[] = [];
    for (let day = 0; day < 7; day += 1) {
      const date = new Date(thisSunday);
      date.setDate(thisSunday.getDate() - week * 7 + day);
      days.push(dateKey(date));
    }
    weeks.push(days);
  }

  return (
    <div className="heatmap-wrap" role="img" aria-label="Practice activity heatmap, darker green means more reps that day">
      <div className="heatmap-rows">
        {rowLabels.map((label, index) => (
          <small key={label} style={{ visibility: index % 2 === 0 ? "visible" : "hidden" }}>{label}</small>
        ))}
      </div>
      <div className="heatmap">
        {weeks.map((week) => (
          <div className="heatmap-col" key={week[0]}>
            {week.map((day) => {
              const count = activity[day] ?? 0;
              const future = day > todayKey;
              const level = future ? 0 : Math.min(count, 3);
              return (
                <span
                  className={`heat-cell lv${level} ${day === todayKey ? "today" : ""} ${future ? "future" : ""}`}
                  key={day}
                  title={`${day} · ${count} ${count === 1 ? "rep" : "reps"}`}
                />
              );
            })}
          </div>
        ))}
      </div>
      <div className="heatmap-legend">
        <small>Less</small>
        <span className="heat-cell lv0" />
        <span className="heat-cell lv1" />
        <span className="heat-cell lv2" />
        <span className="heat-cell lv3" />
        <small>More</small>
      </div>
    </div>
  );
}

type MicState = "idle" | "recording" | "transcribing";

function pickAudioMimeType(): string {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) ?? "";
}

function MicField({
  value,
  onChange,
  label,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder: string;
}) {
  const [state, setState] = useState<MicState>("idle");
  const [error, setError] = useState("");
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("This browser cannot record audio — type instead.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickAudioMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        setState("transcribing");
        try {
          const text = await transcribeAudio(blob);
          if (text) {
            onChange(value ? `${value.trimEnd()} ${text}` : text);
          } else {
            setError("Didn't catch that — try again a little closer to the mic.");
          }
        } catch {
          setError("Transcription failed. Check connection and retry.");
        }
        setState("idle");
      };
      recorderRef.current = recorder;
      recorder.start();
      setState("recording");
    } catch {
      setError("Microphone permission denied.");
    }
  };

  const toggle = () => {
    if (state === "recording") {
      if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    } else if (state === "idle") {
      void startRecording();
    }
  };

  return (
    <div className={`mic-field ${state}`}>
      <textarea
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      <button
        type="button"
        className="mic-button"
        onClick={toggle}
        disabled={state === "transcribing"}
        aria-label={state === "recording" ? "Stop recording" : "Start recording"}
      >
        {state === "idle" && <Microphone weight="fill" />}
        {state === "recording" && <Stop weight="fill" />}
        {state === "transcribing" && <CircleNotch className="spin" weight="bold" />}
      </button>
      <p className="mic-status">
        {state === "recording" && "Listening... tap to stop"}
        {state === "transcribing" && "Transcribing..."}
        {state === "idle" && (error || "Tap the mic and just talk — typing is optional.")}
      </p>
    </div>
  );
}

function TodayView({ data, onStart }: { data: BootstrapData; onStart: () => void }) {
  const lesson = data.content.lessons[0];
  const percent = Math.round((data.progress.completedSteps / data.progress.totalSteps) * 100);
  const allDone = data.progress.completedSteps >= data.progress.totalSteps;
  const activity = data.progress.activityByDate ?? {};
  const totalReps = Object.values(activity).reduce((sum, count) => sum + count, 0);

  return (
    <main className="page today-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">DAY {String(lesson.day).padStart(2, "0")} · FOUNDATION</p>
          <h1>Good morning, Mellie.</h1>
          <p>One technical idea. Three useful phrases. One clearer answer.</p>
        </div>
        <div className="heading-streak"><Fire weight="fill" /> {data.progress.streak} day streak</div>
      </header>

      <section className="heatmap-section">
        <div className="section-heading">
          <div>
            <p className="eyebrow">CHECK-IN</p>
            <h2>Practice heat</h2>
          </div>
          <span>{data.progress.streak} day streak · {totalReps} total reps</span>
        </div>
        <PracticeHeatmap activity={activity} />
      </section>

      <section className={`mission-panel ${allDone ? "done" : ""}`}>
        <div className="mission-copy">
          <div className="mission-meta">
            {allDone && <span className="done-chip"><Check weight="bold" /> CHECKED IN</span>}
            <span><Cloud /> CLOUD NETWORK</span>
            <span><Timer /> {data.settings.dailyMinutes} MIN</span>
          </div>
          <h2>{allDone ? "You crushed it today!" : lesson.title}</h2>
          <p>{allDone ? "Today's route is complete. Extra reps still count as practice." : lesson.subtitle}</p>
          <button className="primary-action" onClick={onStart}>
            {allDone ? "Review again — extra reps" : percent > 0 ? "Continue today's route" : "Start today's route"}
            <ArrowRight weight="bold" />
          </button>
          <div className="mission-progress">
            <div><span style={{ width: `${percent}%` }} /></div>
            <small>{allDone ? "checked in" : `${percent}% saved`}</small>
          </div>
        </div>
        <div className="mission-assistant">
          <span className="speech-note">{allDone ? "Same time tomorrow?" : "Small hops every day. You've got this!"}</span>
          <RabbitAssistant />
        </div>
        <Sparkle className="hero-star one" weight="fill" />
        <Sparkle className="hero-star two" weight="fill" />
      </section>

      <section className="daily-route">
        <div className="section-heading">
          <div>
            <p className="eyebrow">TODAY'S ROUTE</p>
            <h2>Three small hops</h2>
          </div>
          <span>adjusted to {data.settings.dailyMinutes} minutes</span>
        </div>
        <div className="route-line">
          {lesson.stages.map((stage, index) => {
            const done = index < data.progress.completedSteps;
            const active = index === data.progress.completedSteps;
            return (
              <article className={`route-step ${done ? "done" : ""} ${active ? "active" : ""}`} key={stage.id}>
                <span className="route-index">{done ? <Check weight="bold" /> : String(index + 1).padStart(2, "0")}</span>
                <div>
                  <h3>{stage.label}</h3>
                  <p>{stage.instruction}</p>
                </div>
                <small>{scaledMinutes(stage.minutes, data.settings.dailyMinutes)} min</small>
              </article>
            );
          })}
        </div>
      </section>

    </main>
  );
}

function StageContent({ stage, lesson }: { stage: LessonStage; lesson: Lesson }) {
  const [retell, setRetell] = useState("");
  const [answer, setAnswer] = useState("");

  if (stage.id === "input") {
    return (
      <div className="exercise-content listening-content">
        <p className="content-label">READ IT, THEN RETELL IT</p>
        <blockquote>{lesson.listeningText}</blockquote>
        <MicField
          label="Retell the explanation aloud"
          placeholder="Your retelling appears here as you talk..."
          value={retell}
          onChange={setRetell}
        />
        <details><summary>查看中文辅助</summary><p>{lesson.listeningTranslation}</p></details>
      </div>
    );
  }

  if (stage.id === "phrases") {
    return (
      <div className="exercise-content phrase-list">
        {lesson.phrases.map((phrase, index) => (
          <article key={phrase.english}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <div><small>{phrase.intent}</small><h3>{phrase.english}</h3><p>{phrase.chinese}</p></div>
          </article>
        ))}
        <p className="aloud-hint">Say each phrase aloud three times, then hit Save and continue.</p>
      </div>
    );
  }

  return (
    <div className="exercise-content roleplay-content">
      <span className="persona">{lesson.roleplay.persona}</span>
      <p>{lesson.roleplay.setup}</p>
      <blockquote>“{lesson.roleplay.opening}”</blockquote>
      <p className="frame-hint">
        <strong>{lesson.logicFrame.name}</strong>
        {lesson.logicFrame.pattern}
      </p>
      <MicField
        label="Answer the customer aloud"
        placeholder="Your answer appears here as you talk..."
        value={answer}
        onChange={setAnswer}
      />
      <details><summary>Show a model answer</summary><p>{lesson.logicFrame.example}</p></details>
    </div>
  );
}

function PracticeView({
  data,
  onProgress,
}: {
  data: BootstrapData;
  onProgress: (progress: LessonProgress) => void;
}) {
  const lesson = data.content.lessons[0];
  const activeIndex = Math.min(data.progress.completedSteps, lesson.stages.length - 1);
  const [selectedIndex, setSelectedIndex] = useState(activeIndex);
  const [cheer, setCheer] = useState<"first" | "extra" | null>(null);
  const selectedStage = lesson.stages[selectedIndex];

  const allDoneToday = data.progress.completedSteps >= lesson.stages.length;

  const restartRep = () => {
    const progress = { ...data.progress, completedSteps: 0 };
    onProgress(progress);
    void saveProgress(progress);
    setSelectedIndex(0);
  };

  const completeStep = async () => {
    const nextCompleted = Math.max(data.progress.completedSteps, selectedIndex + 1);
    const isFirstCompletion = nextCompleted > data.progress.completedSteps;
    const finishedRoute = nextCompleted >= lesson.stages.length;
    const today = dateKey();
    const alreadyCheckedIn = data.progress.lastCompletedDate === today;

    let { streak, lastCompletedDate, activityByDate } = data.progress;
    if (isFirstCompletion && finishedRoute) {
      // Every finished route deepens today's heat, including extra reps.
      activityByDate = { ...activityByDate, [today]: (activityByDate?.[today] ?? 0) + 1 };
      if (!alreadyCheckedIn) {
        const yesterday = dateKey(new Date(Date.now() - 86_400_000));
        streak = lastCompletedDate === yesterday ? streak + 1 : 1;
        lastCompletedDate = today;
      }
    }

    const nextProgress = {
      ...data.progress,
      completedSteps: nextCompleted,
      flareSeeds: data.progress.flareSeeds + (isFirstCompletion ? 8 : 0),
      streak,
      lastCompletedDate,
      activityByDate,
    };
    onProgress(nextProgress);
    await saveProgress(nextProgress);
    if (isFirstCompletion && finishedRoute) {
      setCheer(alreadyCheckedIn ? "extra" : "first");
    } else if (selectedIndex < lesson.stages.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  return (
    <main className="page practice-page">
      <header className="page-heading practice-heading">
        <div><p className="eyebrow">GUIDED PRACTICE</p><h1>{lesson.title}</h1><p>{lesson.subtitle}</p></div>
        <span className="phase-chip">Phase 01 · Foundation</span>
      </header>

      {allDoneToday && (
        <div className="done-banner">
          <img src="/rabbit-cheer.svg" alt="" />
          <div>
            <strong>Checked in for today!</strong>
            <span>{data.progress.streak} day streak · every extra round deepens today's green.</span>
          </div>
          <button className="secondary-action" onClick={restartRep}>Extra round</button>
        </div>
      )}

      <div className="practice-layout">
        <aside className="stage-rail">
          {lesson.stages.map((stage, index) => (
            <button
              className={`${selectedIndex === index ? "selected" : ""} ${index < data.progress.completedSteps ? "complete" : ""}`}
              key={stage.id}
              onClick={() => setSelectedIndex(index)}
            >
              <span>{index < data.progress.completedSteps ? <CheckCircle weight="fill" /> : String(index + 1).padStart(2, "0")}</span>
              <div><strong>{stage.label}</strong><small>{scaledMinutes(stage.minutes, data.settings.dailyMinutes)} min</small></div>
            </button>
          ))}
        </aside>

        <section className="exercise-panel">
          <div className="exercise-heading">
            <div><p className="eyebrow">STEP {String(selectedIndex + 1).padStart(2, "0")}</p><h2>{selectedStage.label}</h2></div>
            <span>{selectedStage.instruction}</span>
          </div>
          <StageContent stage={selectedStage} lesson={lesson} />
          <footer className="exercise-actions">
            <span><Sparkle /> +8 Flare Seeds for first completion</span>
            <button className="primary-action" onClick={completeStep}>Save and continue <ArrowRight /></button>
          </footer>
        </section>
      </div>

      {cheer && (
        <div className="cheer-overlay" role="dialog" aria-label="Route complete">
          {confettiPieces.map((piece, index) => (
            <span
              className={`confetti-piece ${piece.round ? "round" : ""}`}
              key={index}
              style={{ left: `${piece.left}%`, background: piece.color, animationDelay: `${piece.delay}s` }}
            />
          ))}
          <div className="cheer-card">
            <img src="/rabbit-cheer.svg" alt="Celebrating rabbit" />
            <h2 className="cheer-title">{cheer === "first" ? "You did it!" : "+1 extra rep!"}</h2>
            <p>{cheer === "first" ? "Today's route is complete. Your future self says thank you." : "Today's green just got deeper. Beast mode."}</p>
            <button className="primary-action" onClick={() => setCheer(null)}>Keep hopping <ArrowRight /></button>
          </div>
        </div>
      )}
    </main>
  );
}

function SkillsView({ content }: { content: ContentPack }) {
  return (
    <main className="page skills-page">
      <header className="page-heading"><div><p className="eyebrow">LEARNING MAP</p><h1>Build the language around the job.</h1><p>Technical understanding and communication grow together.</p></div></header>
      <section className="skill-map">
        {content.tracks.map((track, index) => {
          const tone = laneTones[index % laneTones.length];
          return (
            <article key={track.id} className={`skill-lane lane-${index + 1}`}>
              <div className="skill-icon"><img src={rabbitMarks[tone]} alt={`${track.label} rabbit`} /></div>
              <div className="skill-copy"><span>TRACK {String(index + 1).padStart(2, "0")}</span><h2>{track.label}</h2><p>{track.description}</p></div>
              <div className="skill-meter"><strong>{track.progress}%</strong><div><span style={{ width: `${track.progress}%` }} /></div></div>
            </article>
          );
        })}
      </section>
      <section className="phase-path">
        <p className="eyebrow">PHASED MATERIAL</p>
        <div><strong>01</strong><h3>Foundation</h3><p>Explain core concepts accurately in short, structured answers.</p><span>ACTIVE</span></div>
        <div><strong>02</strong><h3>Customer conversations</h3><p>Discovery, objections, comparisons and recommendation language.</p><span>PLANNED</span></div>
        <div><strong>03</strong><h3>Executive influence</h3><p>Lead with outcomes, navigate ambiguity and drive next steps.</p><span>PLANNED</span></div>
      </section>
    </main>
  );
}

function LibraryView({ lesson }: { lesson: Lesson }) {
  return (
    <main className="page library-page">
      <header className="page-heading"><div><p className="eyebrow">PHRASE VAULT</p><h1>Useful language, filed by intent.</h1><p>Save complete thought patterns, not isolated vocabulary.</p></div></header>
      <section className="vault-toolbar"><span>3 phrases · Foundation · Cloud Network</span><button className="secondary-action">All intents</button></section>
      <section className="vault-list">
        {lesson.phrases.map((phrase, index) => (
          <article key={phrase.english}>
            <span className="vault-number">{String(index + 1).padStart(2, "0")}</span>
            <div><small>{phrase.intent}</small><h2>{phrase.english}</h2><p>{phrase.chinese}</p></div>
          </article>
        ))}
      </section>
    </main>
  );
}

function SettingsView({
  data,
  onSettings,
  onContent,
}: {
  data: BootstrapData;
  onSettings: (settings: UserSettings) => void;
  onContent: (content: ContentPack) => void;
}) {
  const [minutes, setMinutes] = useState(data.settings.dailyMinutes);
  const [phase, setPhase] = useState(data.settings.activePhase);
  const [status, setStatus] = useState("");

  const submit = async () => {
    const settings = await saveSettings({ dailyMinutes: minutes, activePhase: phase });
    onSettings(settings);
    setStatus("Saved");
  };

  const importPack = async (file?: File) => {
    if (!file) return;
    try {
      const content = JSON.parse(await file.text()) as ContentPack;
      const saved = await replaceContentPack(content);
      onContent(saved);
      setStatus(`Imported ${saved.title} v${saved.version}`);
    } catch {
      setStatus("Import failed. Check the content pack format and API connection.");
    }
  };

  return (
    <main className="page settings-page">
      <header className="page-heading"><div><p className="eyebrow">YOUR LAB</p><h1>Make the routine fit the day.</h1><p>Duration and learning stages are configuration, not hard-coded course rules.</p></div></header>
      <div className="settings-grid">
        <section className="settings-section duration-setting">
          <p className="eyebrow">DAILY DURATION</p>
          <div className="duration-readout"><strong>{minutes}</strong><span>minutes</span></div>
          <input aria-label="Daily practice duration" type="range" min="3" max="30" step="1" value={minutes} onChange={(event) => setMinutes(Number(event.target.value))} />
          <div className="range-labels"><span>Quick check-in</span><span>Deep practice</span></div>
        </section>
        <details className="settings-section advanced-settings">
          <summary>Advanced</summary>
          <label htmlFor="phase">Material stage</label>
          <select id="phase" value={phase} onChange={(event) => setPhase(event.target.value)}>
            <option value="foundation">01 · Foundation</option>
            <option value="customer-conversations">02 · Customer conversations</option>
            <option value="executive-influence">03 · Executive influence</option>
          </select>
          <p>The active content pack controls lessons while your historical progress stays intact.</p>
          <label className="upload-control">
            <UploadSimple /> Import a new JSON content pack
            <input type="file" accept="application/json" onChange={(event) => importPack(event.target.files?.[0])} />
          </label>
          <small>{data.content.title} · v{data.content.version} · updated {data.content.updatedAt}. New material can be updated independently from the app code.</small>
        </details>
        <section className="settings-section mascot-setting">
          <img src="/rabbit-cheer.svg" alt="Cheering rabbit mascot" />
          <div><p className="eyebrow">YOUR STUDY BUDDY</p><h2>Bunny, your lab rabbit</h2><p>An original paper-cut friend with a Miffy-like heart. It cheers every time you finish a step.</p></div>
        </section>
      </div>
      <div className="settings-save"><span>{status}</span><button className="primary-action" onClick={submit}>Save preferences</button></div>
    </main>
  );
}

function App() {
  const [view, setView] = useState<View>(getInitialView);
  const [data, setData] = useState<BootstrapData | null>(null);

  useEffect(() => {
    let active = true;
    loadBootstrap().then((result) => {
      if (!active) return;
      const today = dateKey();
      // New day, fresh route: yesterday's completion resets the step counter
      // but keeps streak and check-in history.
      if (result.progress.lastCompletedDate && result.progress.lastCompletedDate !== today && result.progress.completedSteps > 0) {
        result = { ...result, progress: { ...result.progress, completedSteps: 0 } };
      }
      // Migrate legacy check-in list to rep counts.
      if (!result.progress.activityByDate && result.progress.completedDates?.length) {
        const activityByDate: Record<string, number> = {};
        for (const day of result.progress.completedDates) activityByDate[day] = 1;
        result = { ...result, progress: { ...result.progress, activityByDate } };
      }
      setData(result);
    });
    return () => {
      active = false;
    };
  }, []);

  if (!data) {
    return <div className="loading-screen"><RabbitAssistant compact tone="yellow" /><p>Preparing the edge lab...</p></div>;
  }

  const changeView = (nextView: View) => {
    window.history.replaceState(null, "", `#${nextView}`);
    startTransition(() => setView(nextView));
  };

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-lockup"><RabbitAssistant compact /><div><strong>WorkingEnglish</strong><span>MELLIE'S LAB</span></div></div>
        <nav>
          {navigation.map((item) => {
            const Icon = item.icon;
            return <button key={item.id} className={view === item.id ? "active" : ""} onClick={() => changeView(item.id)}><Icon /><span>{item.label}</span></button>;
          })}
        </nav>
        <div className="sidebar-foot">
          <span className="phase-tag">PHASE 01</span>
          <p>Explain the complex part clearly.</p>
          <small>{data.user.email}</small>
        </div>
      </aside>

      <section className="content-shell">
        <header className="topbar">
          <button className="mobile-brand" onClick={() => changeView("today")}><RabbitAssistant compact /><span>WorkingEnglish</span></button>
          <span className="topbar-context">PRIVATE WORK ENGLISH LAB</span>
          <div><span><Fire weight="fill" /> {data.progress.streak}</span><span><Sparkle weight="fill" /> {data.progress.flareSeeds}</span></div>
        </header>

        {view === "today" && (
          <TodayView
            data={data}
            onStart={() => {
              // Extra rep: today's route is done, so restart the step counter for another round.
              if (data.progress.completedSteps >= data.progress.totalSteps) {
                const progress = { ...data.progress, completedSteps: 0 };
                setData({ ...data, progress });
                void saveProgress(progress);
              }
              changeView("practice");
            }}
          />
        )}
        {view === "practice" && <PracticeView data={data} onProgress={(progress) => setData({ ...data, progress })} />}
        {view === "skills" && <SkillsView content={data.content} />}
        {view === "library" && <LibraryView lesson={data.content.lessons[0]} />}
        {view === "settings" && (
          <SettingsView
            data={data}
            onSettings={(settings) => setData({ ...data, settings })}
            onContent={(content) => setData({ ...data, content })}
          />
        )}

        <nav className="bottom-nav">
          {navigation.map((item) => {
            const Icon = item.icon;
            return <button key={item.id} className={view === item.id ? "active" : ""} onClick={() => changeView(item.id)}><Icon /><span>{item.label}</span></button>;
          })}
        </nav>
      </section>
    </div>
  );
}

export default App;
